<template>
  <div class="pagination-control">
    <div class="record-count">
      <span v-if="totalItems > 0">
        {{ startRecord }}-{{ endRecord }} of {{ totalItems }}
      </span>
      <span v-else>
        No records
      </span>
    </div>
    <div class="pagination-controls">
      <button class="button page-button nav-button" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)"
        aria-label="Previous page">
        Prev
      </button>
      <button v-for="(page, idx) in visiblePages" :key="idx" class="button page-button"
        :class="{ active: page === currentPage }" @click="goToPage(page)">
        {{ page }}
      </button>
      <button class="button page-button nav-button" :disabled="currentPage === totalPages"
        @click="goToPage(currentPage + 1)" aria-label="Next page">
        Next
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaginationControl',
  props: {
    currentPage: {
      type: Number,
      required: true,
      validator: (value) => value >= 1
    },
    totalItems: {
      type: Number,
      required: true,
      validator: (value) => value >= 0
    },
    itemsPerPage: {
      type: Number,
      required: true,
      validator: (value) => value >= 1
    },
    maxVisiblePages: {
      type: Number,
      default: 5,
      validator: (value) => value >= 1
    }
  },
  computed: {
    totalPages() {
      if (this.totalItems === 0) return 1;
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    startRecord() {
      if (this.totalItems === 0) return 0;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    endRecord() {
      if (this.totalItems === 0) return 0;
      return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const max = Math.min(this.maxVisiblePages, total);

      // If total pages fit within max visible, show all
      if (total <= max) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      // Calculate start and end of visible range
      let start = Math.max(1, current - Math.floor(max / 2));
      let end = start + max - 1;

      // Adjust if we're at the end
      if (end > total) {
        end = total;
        start = Math.max(1, end - max + 1);
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }
  },
  methods: {
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.$emit('page-change', page);
      }
    }
  }
}
</script>

<style scoped>
.pagination-control {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
}

.record-count {
  font-size: 14px;
  color: #595959;
}

.pagination-controls {
  display: flex;
  gap: 4px;
  align-items: center;
}

.page-button {
  min-width: 32px;
  padding: 4px 8px;
}

.page-button.active {
  background-color: var(--primary-color);
  color: #fff;
  border-color: var(--primary-color);
  box-shadow: 0 2px 0px rgb(49, 41, 131);
}

.page-button.active:hover {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  cursor: default;
}

.nav-button {
  font-weight: bold;
}
</style>
